// Material
import { InputAdornment, Tooltip } from '@mui/material';

// Local
import { useController, useFormContext, useWatch } from 'react-hook-form';
import * as S from '../globals/styles/Input.styled';
import { Icon, BaseInput } from '@core-web/ui';
import { IAdornment, IInput } from '../globals/types';
import { useEffect, useState, memo } from 'react';

const HIDDEN_TYPE = 'hidden';

const StartAdornment = ({ icon }: IAdornment) => {
  return (
    <>
      {icon && (
        <S.AdornmentWrapper position='start'>
          <Icon icon={icon} />
        </S.AdornmentWrapper>
      )}
    </>
  );
};

const EndAdornment = ({ icon }: IAdornment) => {
  if (icon) {
    return (
      <InputAdornment position='end'>
        <Icon
          icon={icon}
          sx={{
            display: 'flex',
          }}
        />
      </InputAdornment>
    );
  }
  return null;
};

const BaseTextInput = (props: IInput): JSX.Element => {
  const {
    styles,
    name,
    disabled,
    endAdornment,
    startAdornment,
    startIcon,
    defaultValue,
    placeHolder,
    onChange,
    onBlur,
    withRequiredBorder,
    dependency,
    dependentFields,
    dynamicValueCallback,
    dataSource,
    decimalLimit,
    isDisabled,
    shouldNotSkipHiddenTypeReset,
    error,
    shouldValidateOnChange,
    onValueChange,
    disableResetFieldWhenDependencyValueIsCleared,
    variant,
    skipDefaultReset,
    showToolTip,
    ...restProps
  } = props;

  const [hasValue, setHasValue] = useState(false);
  const { control, setValue, resetField, trigger, register } = useFormContext();
  const { field } = useController({
    control,
    name,
    //defaultValue,
  });

  const updateHasValue = () => {
    if (inputValue !== '' && inputValue !== null && inputValue !== undefined) {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  };

  const inputValue = field.value;
  const dependencyValue = useWatch({ name: dependency || `${name}-dependency` });
  const dataSourceValue = useWatch({ name: dataSource || `${name}-dataSource` });

  useEffect(() => {
    if ((restProps.type !== HIDDEN_TYPE || shouldNotSkipHiddenTypeReset) && !skipDefaultReset) {
      resetField(name, { defaultValue });
    }
  }, [defaultValue]);

  useEffect(() => {
    updateHasValue();

    onValueChange?.(inputValue, dependentFields);
  }, [inputValue]);

  useEffect(() => {
    if (dataSource) {
      setValue(name, dataSourceValue?.label || dataSourceValue || '');
    }
  }, [dataSource, dataSourceValue]);

  useEffect(() => {
    if (dependency && !dependencyValue) {
      if (!disableResetFieldWhenDependencyValueIsCleared) {
        setValue(name, '');
      }
    } else if (dependency && dependencyValue && dynamicValueCallback) {
      setValue(name, dynamicValueCallback);
    }
  }, [dependency, dependencyValue, dynamicValueCallback]);

  useEffect(() => {
    if (decimalLimit && field.value) {
      const newFieldValue = typeof field.value === 'string' ? Number(field.value) : field.value;
      const splitNewFieldValue = newFieldValue.toString().split('.');
      const fieldLength = splitNewFieldValue?.length ? splitNewFieldValue[1]?.length : 0;
      if (!Number.isInteger(newFieldValue) && fieldLength >= decimalLimit) {
        setValue(name, newFieldValue.toFixed(decimalLimit));
      }
    }
  }, [decimalLimit]);

  const handleOnChange = (e: any) => {
    const inputValue = e.target.value;
    const splitValues = inputValue.split('.');

    if (
      restProps?.type === 'number' &&
      inputValue &&
      restProps?.inputProps &&
      (Number(inputValue) < restProps?.inputProps?.min || Number(inputValue) > restProps?.inputProps?.max)
    ) {
      return;
    }
    if (decimalLimit) {
      const hasDecimalValues =
        (typeof inputValue === 'string' || typeof inputValue === 'number') && splitValues?.length;
      const decimalValues = hasDecimalValues ? splitValues[1]?.length : 0;
      if (inputValue && decimalValues > decimalLimit) return;
    }

    onChange?.(e, dependentFields);
    //field.onChange(e);
    if (shouldValidateOnChange) {
      trigger(name);
    }
  };

  const handleOnBlur = (e: any) => {
    updateHasValue();
    onBlur?.(e, dependentFields);
    // field.onBlur();
  };

  const inputBaseProps = {
    sx: styles?.input,
    startAdornment: startAdornment || <StartAdornment icon={startIcon} />,
    endAdornment: endAdornment || <EndAdornment disabled={disabled} />,
    disableInjectingGlobalStyles: true,
    disabled,
    placeholder: restProps?.inputProps?.max ? `${placeHolder}, max - ${restProps?.inputProps?.max}` : placeHolder,
    ...restProps,
    inputProps: restProps?.type === 'number' ? { ...restProps?.inputProps, step: 'any' } : restProps?.inputProps,
  };

  const renderInputField = () => {
    if (restProps.type === HIDDEN_TYPE) {
      return <input {...field} type={HIDDEN_TYPE} />;
    }

    if (showToolTip || restProps.readOnly) {
      return (
        <Tooltip title={inputValue}>
          <S.StyledInput
            {...register(props.name, { onChange: handleOnChange, onBlur: handleOnBlur })}
            {...inputBaseProps}
            className={hasValue ? 'hasValue' : withRequiredBorder ? 'withRequiredBorder' : ''}
            disabled={isDisabled}
            error={!!error}
            value={inputValue ?? ''}
            variants={variant && typeof variant === 'function' ? variant(inputValue) : variant}
          />
        </Tooltip>
      );
    } else {
      return (
        <S.StyledInput
          {...register(props.name, { onChange: handleOnChange, onBlur: handleOnBlur })}
          {...inputBaseProps}
          className={hasValue ? 'hasValue' : withRequiredBorder ? 'withRequiredBorder' : ''}
          disabled={isDisabled}
          error={!!error}
          variants={variant && typeof variant === 'function' ? variant(inputValue) : variant}
        />
      );
    }
  };

  return renderInputField();
};

const TextInput = memo((props: IInput): JSX.Element => {
  return <BaseInput {...props} input={BaseTextInput} />;
});

export default TextInput;

TextInput.displayName = 'TextInput';
